import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';

import { ageBucketMeta } from '../../dto/openInvoice';
import OpenInvoiceFilters from './openInvoice/OpenInvoiceFilters';
import OpenInvoiceSummaryCard from './openInvoice/OpenInvoiceSummaryCard';
import OpenInvoiceTable from './openInvoice/OpenInvoiceTable';
import useDunningLevels from './openInvoice/useDunningLevels';
import useOpenInvoiceFilters, { OVERDUE_BUCKETS } from './openInvoice/useOpenInvoiceFilters';
import useOpenInvoiceMetrics from './openInvoice/useOpenInvoiceMetrics';
import useOpenInvoices from './openInvoice/useOpenInvoices';
import useOpenInvoiceSorting from './openInvoice/useOpenInvoiceSorting';
import { buildCsvRow, downloadCsv, fmtCurrency, fmtDate } from './openInvoice/openInvoiceFormatters';

export default function OpenInvoiceOverview() {
    const [searchParams] = useSearchParams();
    const preset = searchParams.get('preset');
    const initialFilters = useMemo(() => {
        if (preset === 'overdue') {
            return { buckets: OVERDUE_BUCKETS };
        }
        return null;
    }, [preset]);

    const {
        filters,
        appliedFilters,
        handleFilterChange,
        handleDateChange,
        applyFilters,
        resetFilters,
    } = useOpenInvoiceFilters(initialFilters);
    const levels = useDunningLevels();
    const { invoices, loading, error } = useOpenInvoices(appliedFilters);
    const { pieData, totalOutstanding } = useOpenInvoiceMetrics(invoices);
    const { toggleSort, sortedInvoices } = useOpenInvoiceSorting(invoices);

    const handleExport = () => {
        const header = buildCsvRow([
            'Rechnungsnummer',
            'Kunde',
            'Rechnungsdatum',
            'Fällig am',
            'Tage Überfälligkeit',
            'Bucket',
            'Mahnstufe',
            'Offener Betrag',
        ]);
        const rows = sortedInvoices.map((inv) => buildCsvRow([
            inv.invoiceNumber,
            inv.customerName,
            fmtDate(inv.invoiceDate),
            fmtDate(inv.dueDate),
            inv.daysOverdue,
            ageBucketMeta[inv.ageBucket]?.label ?? inv.ageBucket,
            inv.dunningLevelName || (inv.dunningLevelId ? 'Mahnstufe' : 'Keine'),
            fmtCurrency(inv.outstandingAmount),
        ]));
        const filename = `offene-posten-${new Date().toISOString().slice(0, 10)}.csv`;
        downloadCsv([header, ...rows], filename);
    };

    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Offene Posten
            </Typography>
            <Typography variant="body1" color="text.secondary">
                Übersicht über alle offenen Forderungen mit Filtermöglichkeiten, Export und einer kompakten Fälligkeitsstruktur.
            </Typography>

            <Stack spacing={3} sx={{ mt: 4 }}>
                <OpenInvoiceFilters
                    filters={filters}
                    levels={levels}
                    onFilterChange={handleFilterChange}
                    onDateChange={handleDateChange}
                    onApply={applyFilters}
                    onReset={resetFilters}
                />

                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="stretch">
                    <OpenInvoiceSummaryCard
                        loading={loading}
                        pieData={pieData}
                        totalOutstanding={totalOutstanding}
                    />
                    <OpenInvoiceTable
                        loading={loading}
                        error={error}
                        sortedInvoices={sortedInvoices}
                        onExport={handleExport}
                        onToggleSort={toggleSort}
                    />
                </Stack>
            </Stack>
        </Box>
    );
}
